import { ApiSdkEvents, TaskCollection, TaskState } from '@ekkogmbh/apisdk';
import { createContextHardened } from '../Helper/ContextHelper';
import { useCallback, useEffect, useRef, useState } from 'react';
import useRequest from '../Hooks/useRequest';
import useEkko from './EkkoProvider';
import { enqueueSnackbar } from 'notistack';

export type TaskCollectionProgressCallback = (id: string, state: string, progress?: number) => void;

const stateEventMap: Record<TaskState, ApiSdkEvents> = {
  [TaskState.CREATED]: ApiSdkEvents.TASK_COLLECTION_CREATED,
  [TaskState.FINISHED]: ApiSdkEvents.TASK_COLLECTION_FINISHED,
  [TaskState.FAILED]: ApiSdkEvents.TASK_COLLECTION_FAILED,
  [TaskState.RUNNING]: ApiSdkEvents.TASK_COLLECTION_PROGRESS,
};
export const mapTaskStateToEvent = (state: TaskState): ApiSdkEvents =>
  stateEventMap[state] ?? ApiSdkEvents.TASK_COLLECTION_ERROR;

interface Context {
  taskCollections: TaskCollection[];
  /**
   * Subscribe to task collection progress polling.
   * URI is nullable for ApiSdk compliance.
   * When not providing a callback, the state is still polled.
   */
  subscribe: (uri: string | null, callback?: TaskCollectionProgressCallback) => void;
  unsubscribe: (uri: string) => void;
}

const [taskCollectionContext, useTaskCollections] = createContextHardened<Context>('Task Collection');
export default useTaskCollections;

/** Bundles states per subscription */
interface TaskCollectionSubscription {
  taskCollection?: TaskCollection;
  id: number;
  callback?: TaskCollectionProgressCallback;
}

const pollDelay = 2;
interface Props {
  children: React.ReactNode;
}
export const TaskCollectionProvider = ({ children }: Props): React.JSX.Element => {
  const { api } = useEkko();

  /** Subscriptions indexed by URI */
  const subscriptions = useRef<Record<string, TaskCollectionSubscription>>({});
  const intervalRef = useRef<NodeJS.Timeout>();
  const [isPolling, startPolling] = useRequest();

  const [taskCollections, setTaskCollections] = useState<TaskCollection[]>([]);

  const subscribe = useCallback(
    (uri: string | null, callback?: TaskCollectionProgressCallback) => {
      if (uri === null) {
        return;
      }
      const uriParts = uri.split('/');
      const idPart = uriParts[uriParts.length - 1];
      const taskCollectionId = parseInt(idPart, 10);

      if (isNaN(taskCollectionId)) {
        enqueueSnackbar('Could not queue task collection id: ' + taskCollectionId, { variant: 'error' });
        return;
      }

      const sub: TaskCollectionSubscription = {
        id: taskCollectionId,
        callback,
      };

      subscriptions.current[uri] = sub;
    },
    [api],
  );

  const unsubscribe = useCallback((uri: string) => {
    delete subscriptions.current[uri];
  }, []);

  const poll = useCallback(() => {
    if (isPolling || Object.keys(subscriptions.current).length === 0) {
      // skipping an iteration if previous one takes longer...
      return;
    }

    startPolling(
      async () => {
        const tcUpdate: TaskCollection[] = [];
        for (const [uri, sub] of Object.entries(subscriptions.current)) {
          sub.taskCollection = await api.getTaskCollectionProgress(sub.id);
          if (sub.taskCollection !== undefined) {
            const { id, progress, state } = sub.taskCollection;

            if (sub.callback !== undefined) {
              sub.callback(id, state, progress);
            }
            api.emit(mapTaskStateToEvent(state), sub.taskCollection);

            if ([TaskState.FINISHED, TaskState.FAILED].includes(sub.taskCollection.state)) {
              unsubscribe(uri);
            } else {
              subscriptions.current[uri] = sub;

              // add to tracked task collections
              tcUpdate.push(sub.taskCollection);
            }
          }
        }
        setTaskCollections(tcUpdate);
      },
      undefined,
      () => {
        enqueueSnackbar('halting task polling due to error', { variant: 'error' });
        subscriptions.current = {};
        setTaskCollections([]);
      },
    );
  }, [isPolling, api]);

  useEffect(() => {
    intervalRef.current = setInterval(poll, pollDelay * 1000);
    return () => {
      subscriptions.current = {};
      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <taskCollectionContext.Provider
      value={{
        taskCollections,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </taskCollectionContext.Provider>
  );
};
